<template lang="html">
    <div class="page-container">
        <div class="page-container__header">
            <h1 class="header-1 page-container__title">Ajutor</h1>
        </div>
        <div class="page-container__body">
            <Card title="Testare dispozitive">
                <div class="device-test-section">
                    <transition name="show">
                        <div v-if="beginTestingDevices" class="device-test-section__test-devices">
                            <!-- Test devices -->
                            <div class="test-devices">
                                <div class="test-devices__column">
                                    <div class="test-devices__video">
                                        <video autoplay id="testVideoElement"></video>
                                    </div>
                                    <button class="test-devices__button" @click="toggleTestVideo($event)" :disabled="!this.hasVideo || this.videoPermissionStatus == 'denied'">
                                        Test Video
                                    </button>
                                </div>
                                <div class="test-devices__column">
                                    <div class="test-devices__audio">
                                        <canvas id="audioMeter" width="256" height="144"></canvas>
                                        <i class="fas fa-microphone fa-3x"></i>
                                    </div>
                                    <button class="test-devices__button" @click="toggleTestAudio($event)" :disabled="!this.hasMic || this.micPermissionStatus == 'denied'">
                                        Test Audio
                                    </button>
                                </div>
                            </div>

                            <!-- Device permission -->
                            <div class="device-permission">
                                <div class="device-permission__title">Permisiuni dispozitive <i class="fas fa-sync-alt"></i></div>
                                <div class="device-permission__devices" v-dragscroll="!isMobile">
                                    <div
                                        class="device-permission__device"
                                        :class="[{'device-permission__color--success' : this.videoPermissionStatus == 'granted'}, {'device-permission__color--warning' : this.videoPermissionStatus == 'prompt'}, {'device-permission__color--danger' : this.videoPermissionStatus == 'denied'}, {'device-permission__color--disabled' : !this.hasVideo}]"
                                    >
                                        <i class="fas fa-video"></i>
                                    </div>
                                    <div
                                        class="device-permission__device"
                                        :class="[{'device-permission__color--success' : this.micPermissionStatus == 'granted'}, {'device-permission__color--warning' : this.micPermissionStatus == 'prompt'}, {'device-permission__color--danger' : this.micPermissionStatus == 'denied'}, {'device-permission__color--disabled' : !this.hasMic}]"
                                    >
                                        <i class="fas fa-microphone"></i>
                                    </div>
                                    <div class="device-permission__device" :class="[{'device-permission__color--success': this.hasSpeakers}, {'device-permission__color--disabled': !this.hasSpeakers}]">
                                        <i class="fas fa-volume-up"></i>
                                    </div>
                                </div>
                                <div class="device-permission__legend">
                                    <div class="device-permission__legend-item"><i class="fas fa-minus device-permission__color--success"></i>Aplicația poate folosi dispozitivul media.</div>
                                    <div class="device-permission__legend-item"><i class="fas fa-minus device-permission__color--warning"></i>Aplicația necesită permisiunea dvs. pentru a folosi dispozitivul media.</div>
                                    <div class="device-permission__legend-item"><i class="fas fa-minus device-permission__color--danger"></i>Aplicația nu poate folosi dispozitivul media. <span> Detalii..</span></div>
                                    <div class="device-permission__legend-item"><i class="fas fa-minus device-permission__color--disabled"></i>Dispozitivul media nu poate fi detectat.</div>
                                </div>
                            </div>
                        </div>
                    </transition>

                    <!-- Device testing - HELP STEPS -->
                    <div :class="{
                            'device-test-section__steps-container-before-test': !beginTestingDevices,
                            'device-test-section__steps-container': beginTestingDevices
                        }">
                        <div class="device-test-section__title">Testează-ți camera sau microfonul</div>
                        <div class="device-test-section__steps-list">
                            <div class="device-test-section__step">
                                <div class="device-test-section__step__number">1</div>
                                <div>
                                    Apasă pe unul din butoanele <span>“Test Video”</span> sau <span>“Test Audio”</span>
                                    pentru a începe testarea.
                                </div>
                            </div>
                            <div class="device-test-section__step">
                                <div class="device-test-section__step__number">2</div>
                                <div>Asigurați-va că dispozitivele dvs. funcționează corect folosind indicatorii oferiți de platforma <span>NEXYO</span>.</div>
                            </div>
                            <div class="device-test-section__step">
                                <div class="device-test-section__step__number">3</div>
                                <div>Permiteți platformei <span>NEXYO</span> să acceseze dispozitivele dvs. aspasand butonul <span>“allow”</span> sau <span>“permiteți”</span>.</div>
                            </div>
                        </div>

                        <button v-if="!beginTestingDevices" @click="testDevices()"
                            class="device-test-section__steps-container-before-test__button">
                            Incepe testarea dispozitivelor
                        </button>

                        <div class="device-test-section__small-text">*În cazul în care nu ați reușit să permiteți platformei accesul la dispozitivele dvs., consultați pagină <span>“Dispozitive media & Permisiuni”</span>.</div>
                    </div>
                </div>
            </Card>
            <div class="spacer"></div>
            <!-- Legal Pages -->
            <div class="legal-pages" v-arrowscroll>
                <div class="legal-pages__container" v-dragscroll="!isMobile">
                    <LegalCard v-for="(legalPage, i) in pages" :key="i" :title="legalPage.title" :slug="legalPage.slug" />
                </div>
            </div>
            <div class="spacer"></div>

            <!-- FAQ -->
            <Card title="Întrebări frecvente" v-if="this.$store.state.help.categories.length > 0">
                <FaqCategories :categories="categories" @changedCategory="updateCategory" />
                <div class="faq-section__content">
                    <div class="faq-questions">
                        <div class="faq-questions__group" v-for="(group, i) in categories" :key="i">
                            <div class="faq-questions__question" v-for="(faq, j) in group.faqs" :key="j">
                                <FaqQuestion :question="faq.question" :answer="faq.answer" :isLast="(group.faqs.length == 1)" v-if="faq.category == currentCategory || currentCategory == 'all'" />
                            </div>
                        </div>
                    </div>
                    <div class="faq-section__image">
                        <img src="@/assets/images/help-image.svg" alt="Help Nexyo" />
                    </div>
                </div>
            </Card>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from "vuex";
    import { mapActions } from "vuex";

    import Card from "@/components/ui/Card";
    import UiButton from "@/components/ui/Button";
    import LegalCard from "./components/LegalCard";
    import FaqCategories from "./components/FaqCategories";
    import FaqQuestion from "./components/FaqQuestion";

    export default {
        data() {
            return {
                currentCategory: "all",
                hasMic: false,
                hasVideo: false,
                hasSpeakers: false,
                videoPermissionStatus: "unknown",
                micPermissionStatus: "unkwnown",
                videoStream: null,
                audioStream: null,
                beginTestingDevices: false
            };
        },
        components: {
            Card,
            UiButton,
            LegalCard,
            FaqCategories,
            FaqQuestion,
        },
        computed: {
            ...mapGetters({
                pages: "help/pages",
                categories: "help/categories",
                isMobile: "layout/isMobile",
            }),
        },
        methods: {
            ...mapActions({
                fetchTermsPages: "help/fetchTermsPages",
                fetchFaqs: "help/fetchFaqs",
            }),
            updateCategory(value) {
                this.currentCategory = value;
                setTimeout(() => {
                    let displayedQuestions = document.getElementsByClassName("faq-question");
                    displayedQuestions[displayedQuestions.length - 1].style.border = "none";
                }, 100);
            },
            getPermissionsStatus() {
                console.log("requesting permissions..");
                if (navigator && navigator.permissions && navigator.permissions.query) {
                    console.log("Default permissions code");
                    navigator.permissions
                        .query({ name: "microphone" })
                        .then((permissionObj) => {
                            this.micPermissionStatus = permissionObj.state;
                            this.beginTestingDevices = true;
                        })
                        .catch((error) => {
                            console.log("Mic permission error :", error);
                        });

                    navigator.permissions
                        .query({ name: "camera" })
                        .then((permissionObj) => {
                            this.videoPermissionStatus = permissionObj.state;
                            this.beginTestingDevices = true;
                        })
                        .catch((error) => {
                            console.log("Video permission error :", error);
                        });
                } else {
                    //This code is for Safari
                    console.log("Safari permissions code");
                    let VueThis = this;

                    if (this.micPermissionStatus != "granted")
                        navigator.mediaDevices
                            .getUserMedia({ audio: true })
                            .then(function (stream) {
                                //it will start the mic stream and stop it one second after
                                stream.getTracks().forEach(function (track) {
                                    track.stop();
                                });
                                VueThis.micPermissionStatus = "granted";
                                VueThis.beginTestingDevices = true;
                            })
                            .catch(function (err) {
                                console.log(err);
                                VueThis.micPermissionStatus = "denied";
                                VueThis.beginTestingDevices = false;
                            });

                    if (this.videoPermissionStatus != "granted")
                        navigator.mediaDevices
                            .getUserMedia({ video: true })
                            .then(function (stream) {
                                //it will start the mic stream and stop it one second after
                                stream.getTracks().forEach(function (track) {
                                    track.stop();
                                });
                                VueThis.videoPermissionStatus = "granted";
                                VueThis.beginTestingDevices = true;
                            })
                            .catch(function (err) {
                                console.log(err);
                                VueThis.videoPermissionStatus = "denied";
                                VueThis.beginTestingDevices = false;
                            });
                }
            },
            stopVideoTesting() {
                if (this.videoStream)
                    this.videoStream.getTracks().forEach((track) => {
                        track.stop();
                    });
            },
            startVideoTesting() {
                const devices = {
                    video: { width: { min: 1280 }, height: { min: 720 } },
                    audio: false,
                };

                const videoElement = document.getElementById("testVideoElement");

                navigator.mediaDevices
                    .getUserMedia(devices)
                    .then((stream) => {
                        videoElement.srcObject = stream;
                        this.getPermissionsStatus();
                        this.videoStream = stream;
                    })
                    .catch((error) => {
                        this.getPermissionsStatus();
                        console.log(error);
                    });
            },
            startAudioTesting() {
                const devices = {
                    audio: true,
                };

                navigator.mediaDevices
                    .getUserMedia(devices)
                    .then((stream) => {
                        this.getPermissionsStatus();
                        var audioContext = new AudioContext();
                        this.audioStream = stream;
                        var analyser = audioContext.createAnalyser();
                        var microphone = audioContext.createMediaStreamSource(stream);
                        var javascriptNode = audioContext.createScriptProcessor(2048, 1, 1);

                        analyser.smoothingTimeConstant = 0.8;
                        analyser.fftSize = 1024;

                        microphone.connect(analyser);
                        analyser.connect(javascriptNode);
                        javascriptNode.connect(audioContext.destination);

                        let canvasElement = document.getElementById("audioMeter");

                        var canvasContext = canvasElement.getContext("2d");

                        javascriptNode.onaudioprocess = function () {
                            var array = new Uint8Array(analyser.frequencyBinCount);
                            analyser.getByteFrequencyData(array);
                            var values = 0;

                            var length = array.length;
                            for (var i = 0; i < length; i++) {
                                values += array[i];
                            }

                            var average = values / length;

                            canvasContext.clearRect(0, 0, 256, 144);
                            canvasContext.fillStyle = "#15D400";
                            canvasContext.fillRect(0, 144 - average, 256, 144);
                            canvasContext.fillStyle = "#262626";
                        };
                    })
                    .catch((error) => {
                        this.getPermissionsStatus();
                        console.log(error);
                    });
            },
            stopAudioTesting() {
                if (this.audioStream) location.reload();
            },
            checkDeviceSupport() {
                if (navigator && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
                    // Firefox 38+ seems having support of enumerateDevices
                    navigator.enumerateDevices = function (callback) {
                        navigator.mediaDevices.enumerateDevices().then(callback);
                    };
                }

                var MediaDevices = [];
                var isHTTPs = location.protocol === "https:";
                var canEnumerate = false;
                var VueThis = this;

                if (typeof MediaStreamTrack !== "undefined" && "getSources" in MediaStreamTrack) {
                    canEnumerate = true;
                } else if (navigator.mediaDevices && !!navigator.mediaDevices.enumerateDevices) {
                    canEnumerate = true;
                }

                var isMicrophoneAlreadyCaptured = false;
                var isWebcamAlreadyCaptured = false;

                if (!canEnumerate) return;

                if (!navigator.enumerateDevices && window.MediaStreamTrack && window.MediaStreamTrack.getSources) navigator.enumerateDevices = window.MediaStreamTrack.getSources.bind(window.MediaStreamTrack);

                if (!navigator.enumerateDevices && navigator.enumerateDevices) navigator.enumerateDevices = navigator.enumerateDevices.bind(navigator);

                if (!navigator.enumerateDevices) return;

                MediaDevices = [];
                navigator.enumerateDevices(function (devices) {
                    devices.forEach(function (_device) {
                        var device = {};
                        for (var d in _device) {
                            device[d] = _device[d];
                        }

                        if (device.kind === "audio") device.kind = "audioinput";

                        if (device.kind === "video") device.kind = "videoinput";

                        var skip;
                        MediaDevices.forEach(function (d) {
                            if (d.id === device.id && d.kind === device.kind) skip = true;
                        });

                        if (skip) return;

                        if (!device.deviceId) device.deviceId = device.id;

                        if (!device.id) device.id = device.deviceId;

                        if (!device.label) {
                            device.label = "Please invoke getUserMedia once.";
                            if (!isHTTPs) {
                                device.label = "HTTPs is required to get label of this " + device.kind + " device.";
                            }
                        } else {
                            if (device.kind === "videoinput" && !isWebcamAlreadyCaptured) {
                                isWebcamAlreadyCaptured = true;
                            }

                            if (device.kind === "audioinput" && !isMicrophoneAlreadyCaptured) {
                                isMicrophoneAlreadyCaptured = true;
                            }
                        }

                        console.log(device.kind);

                        if (device.kind === "audioinput") VueThis.hasMic = true;

                        if (device.kind === "audiooutput" || navigator.vendor == "Apple Computer, Inc.") VueThis.hasSpeakers = true;

                        if (device.kind === "videoinput") VueThis.hasVideo = true;

                        MediaDevices.push(device);
                    });
                });
            },
            toggleTestVideo(event) {
                let el = event.target;
                if (el.innerText == "STOP") {
                    this.stopVideoTesting();
                    el.innerText = "Test Video";
                } else {
                    this.startVideoTesting();
                    el.innerText = "STOP";
                }
            },
            toggleTestAudio(event) {
                let el = event.target;
                if (el.innerText == "STOP") {
                    this.stopAudioTesting();
                    el.innerText = "Test Audio";
                } else {
                    this.startAudioTesting();
                    el.innerText = "STOP";
                }
            },
            testDevices() {
                this.checkDeviceSupport();
                this.getPermissionsStatus();
            }
        },
        mounted() {
            // this.checkDeviceSupport();
            // this.getPermissionsStatus();
            // this.beginTestingDevices = false;
            this.fetchTermsPages();
            this.fetchFaqs().then(() => {
                if(this.$store.state.help.categories.length > 0)
                    setTimeout(() => {
                        let displayedQuestions = document.getElementsByClassName("faq-question");
                        displayedQuestions[displayedQuestions.length - 1].style.border = "none";
                    }, 1000);
            });
        },
    };
</script>

<style lang="scss" scoped>
    @import "@/scss/pages/help";

    .show-enter-active,
    .show-leave-enter {
        transform: translateX(0);
        transition: all .3s linear;
    }
    .show-enter,
    .show-leave-to {
        transform: translateX(100%);
    }
</style>
